import { useState } from "react";
import { X } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { MetricToggle } from "./MetricToggle";
import { userSeries } from "../lib/csvAnalysis";
import { COLORS, fmtCompact, fmtNum, slug } from "../lib/format";

const daysByMonth = (userDays) => {
  const out = {};
  Object.keys(userDays || {}).forEach((k) => { out[k.slice(0, 7)] = (out[k.slice(0, 7)] || 0) + 1; });
  return out;
};

const CompareTooltip = ({ active, payload, label, metric }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-700 bg-[#0B0F19]/95 backdrop-blur-md px-3 py-2 shadow-xl" data-testid="compare-tooltip">
      <p className="font-mono text-xs text-slate-400 mb-1.5">{label}</p>
      {payload.map((p) => {
        const days = p.payload[`${p.dataKey}__days`];
        return (
          <div key={p.dataKey} className="flex items-center gap-2 text-xs py-0.5">
            <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-300 max-w-[140px] truncate">{p.name}</span>
            <span className="ml-auto pl-4 font-mono text-slate-100">{fmtNum(p.value)}</span>
            {metric !== "days" && days != null && <span className={`font-mono w-14 text-right ${days < 15 ? "text-amber-400" : "text-slate-500"}`}>{days}d</span>}
          </div>
        );
      })}
    </div>
  );
};

export const CompareChart = ({ data, raw, selected, onRemove, onClear, onTop }) => {
  const [metric, setMetric] = useState("visits");
  const users = selected.map((id) => data.users.find((u) => u.id === id)).filter(Boolean);
  const hasDays = !!raw?.days;
  const dayMaps = Object.fromEntries(users.map((u) => [u.id, daysByMonth(raw?.days?.[u.id])]));
  const series = userSeries(data.months, users, metric === "days" ? "visits" : metric, data.capIdx).map((row) => {
    users.forEach((u) => {
      const d = hasDays ? dayMaps[u.id][row.month] || 0 : null;
      row[`${u.id}__days`] = d;
      if (metric === "days") row[u.id] = d;
    });
    return row;
  });
  const opts = [["visits", "Visits"], ...(data.hasQty ? [["qty", "Qty picked"]] : []), ...(data.hasHop ? [["pph", "Picks / hr"]] : []), ...(hasDays ? [["days", "Days worked"]] : [])];
  return (
    <div className="panel p-5 sm:p-6" data-testid="multi-user-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-100">User comparison</h3>
          <p className="text-xs text-slate-500">Tick up to 8 users in the table below</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="select-top-5-button" onClick={onTop} className="text-xs px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">Top 5</button>
          <button data-testid="clear-selection-button" onClick={onClear} className="text-xs px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">Clear</button>
          <MetricToggle value={metric} onChange={setMetric} options={opts} prefix="compare-metric" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4 min-h-[28px]">
        {users.map((u, i) => (
          <span key={u.id} data-testid={`legend-chip-${slug(u.id)}`} className="inline-flex items-center gap-2 text-xs pl-2.5 pr-1 py-1 rounded-full border border-slate-700 bg-[#0B0F19] text-slate-200">
            <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i] }} />{u.id}
            <button data-testid={`remove-chip-${slug(u.id)}`} onClick={() => onRemove(u.id)} className="p-0.5 rounded-full hover:bg-slate-700"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="h-[340px] mt-4">
        {users.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg" data-testid="compare-empty">Select users to compare their monthly trend</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1F2937" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1F2937" }} />
              <YAxis stroke="#64748B" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
              <Tooltip content={<CompareTooltip metric={metric} />} cursor={{ stroke: "#334155" }} />
              {users.map((u, i) => <Line key={u.id} type="monotone" dataKey={u.id} name={u.id} stroke={COLORS[i]} strokeWidth={2.25} dot={false} activeDot={{ r: 5 }} />)}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      {hasDays && users.length > 0 && (
        <div className="mt-5 overflow-x-auto" data-testid="compare-days-strip">
          <p className="label-mono mb-2">Days worked per month <span className="normal-case tracking-normal text-slate-500">· amber = under 15 days (possible leave)</span></p>
          <table className="w-full text-[11px] font-mono">
            <thead><tr className="text-slate-500">
              <th className="text-left py-1 pr-3 font-normal">User</th>
              {series.map((r) => <th key={r.month} className="text-center px-1 font-normal whitespace-nowrap">{r.label}</th>)}
            </tr></thead>
            <tbody>{users.map((u, i) => (
              <tr key={u.id} data-testid={`days-strip-${slug(u.id)}`}>
                <td className="py-1 pr-3 whitespace-nowrap text-slate-300"><span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: COLORS[i] }} />{u.id}</td>
                {series.map((r) => {
                  const d = r[`${u.id}__days`];
                  return <td key={r.month} className="px-1 py-1 text-center"><span className={`inline-block min-w-[28px] rounded px-1 py-0.5 ${!d ? "text-slate-600" : d < 15 ? "bg-amber-500/15 text-amber-300" : "bg-slate-800 text-slate-200"}`}>{d || "—"}</span></td>;
                })}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
